import { useMediaQuery } from "@/hooks/useMediaQuery";

import { useIsVirtualKeyboardVisible } from "@/hooks/useIsVirtualKeyboardVisible";
import { clsx } from "clsx";
import {
  createContext,
  forwardRef,
  useCallback,
  useLayoutEffect,
  useState,
  type HTMLAttributes,
  type ReactNode,
} from "react";
import "./page-frame.css";

export const PageScrollContext = createContext<HTMLElement | null>(null);

type PageFrameProps = HTMLAttributes<HTMLDivElement> & {
  header: ReactNode;
  print?: boolean;
};

// Mobile pages own their scrolling so Safari's fixed-position viewport cannot
// leave the header and actions displaced after keyboard dismissal.
export const PageFrame = forwardRef<HTMLDivElement, PageFrameProps>(
  function PageFrame({ header, children, className, print, ...rest }, ref) {
    const isMobile =
      useMediaQuery("(max-width: 780px), (pointer: coarse)") && !print;
    const keyboardVisible = useIsVirtualKeyboardVisible(undefined, isMobile);
    const [content, setContent] = useState<HTMLElement | null>(null);
    const [frame, setFrame] = useState<HTMLDivElement | null>(null);
    const contentRef = useCallback(
      (element: HTMLDivElement | null) => {
        setContent(element);
        if (typeof ref === "function") {
          ref(element);
        } else if (ref) {
          ref.current = element;
        }
      },
      [ref],
    );

    useLayoutEffect(() => {
      if (!isMobile || !frame) {
        return;
      }
      const viewport = window.visualViewport;
      const updateViewport = () => {
        // Zoom magnifies the existing layout; it must not resize the rows.
        if (viewport && viewport.scale !== 1) {
          return;
        }
        frame.style.setProperty(
          "--page-viewport-height",
          `${viewport?.height ?? window.innerHeight}px`,
        );
        // iOS can pan the document even when this frame owns scrolling.
        frame.style.setProperty("--page-viewport-top", `${window.scrollY}px`);
      };
      updateViewport();
      viewport?.addEventListener("resize", updateViewport);
      viewport?.addEventListener("scroll", updateViewport);
      window.addEventListener("resize", updateViewport);
      window.addEventListener("scroll", updateViewport, { passive: true });
      window.addEventListener("pageshow", updateViewport);
      document.addEventListener("visibilitychange", updateViewport);
      return () => {
        viewport?.removeEventListener("resize", updateViewport);
        viewport?.removeEventListener("scroll", updateViewport);
        window.removeEventListener("resize", updateViewport);
        window.removeEventListener("scroll", updateViewport);
        window.removeEventListener("pageshow", updateViewport);
        document.removeEventListener("visibilitychange", updateViewport);
        frame.style.removeProperty("--page-viewport-height");
        frame.style.removeProperty("--page-viewport-top");
      };
    }, [isMobile, frame]);

    useLayoutEffect(() => {
      if (!isMobile || !content) {
        return;
      }
      let animationFrame: number | undefined;
      const revealFocusedField = () => {
        if (animationFrame !== undefined) {
          window.cancelAnimationFrame(animationFrame);
        }
        animationFrame = window.requestAnimationFrame(() => {
          animationFrame = undefined;
          const focused = document.activeElement;
          if (!(focused instanceof HTMLElement) || !content.contains(focused)) {
            return;
          }
          const bounds = content.getBoundingClientRect();
          const field = focused.getBoundingClientRect();
          if (field.height === 0 || bounds.height === 0) {
            return;
          }
          const bottom = Math.min(field.bottom, field.top + bounds.height);
          content.scrollTop +=
            field.top < bounds.top
              ? field.top - bounds.top
              : Math.max(0, bottom - bounds.bottom);
        });
      };
      const viewport = window.visualViewport;
      let previousHeight = viewport?.height ?? window.innerHeight;
      const onResize = () => {
        const height = viewport?.height ?? window.innerHeight;
        if (height < previousHeight && (!viewport || viewport.scale === 1)) {
          revealFocusedField();
        }
        previousHeight = height;
      };
      content.addEventListener("focusin", revealFocusedField);
      viewport?.addEventListener("resize", onResize);
      if (keyboardVisible) {
        revealFocusedField();
      }
      return () => {
        content.removeEventListener("focusin", revealFocusedField);
        viewport?.removeEventListener("resize", onResize);
        if (animationFrame !== undefined) {
          window.cancelAnimationFrame(animationFrame);
        }
      };
    }, [isMobile, keyboardVisible, content]);

    return (
      <PageScrollContext.Provider value={isMobile ? content : null}>
        <div
          ref={setFrame}
          data-keyboard-visible={isMobile && keyboardVisible}
          className={clsx("page-frame", { "mobile-page-frame": isMobile })}
        >
          {header}
          <main
            ref={contentRef}
            {...rest}
            className={clsx("page-frame-content", className)}
          >
            {children}
          </main>
        </div>
      </PageScrollContext.Provider>
    );
  },
);
